/**
 * 模板列表页面
 */

import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Modal,
  message,
  Image,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { Template, listTemplates, deleteTemplate, batchDeleteTemplates } from '../api/templates';
import { hasPermission } from '../utils/auth';
import dayjs from 'dayjs';

// 分类选项
const CATEGORY_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Male', value: 'Male' },
  { label: 'Female', value: 'Female' },
  { label: 'Halloween', value: 'Halloween' },
  { label: 'Christmas', value: 'Christmas' },
  { label: 'Festival', value: 'Festival' },
  { label: 'Portrait', value: 'Portrait' },
  { label: 'Style', value: 'Style' },
  { label: 'Other', value: 'Other' },
];

const { Title } = Typography;

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [category, setCategory] = useState<string>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  /**
   * 加载模板列表
   */
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await listTemplates({
        page,
        page_size: pageSize,
        category: category || undefined,
      });

      if (response.success) {
        // 客户端过滤（如果需要搜索功能）
        let filteredTemplates = response.data.templates;
        if (searchText) {
          const searchLower = searchText.toLowerCase();
          filteredTemplates = filteredTemplates.filter(
            (t) =>
              t.title.toLowerCase().includes(searchLower) ||
              t.prompt.toLowerCase().includes(searchLower) ||
              t.category.toLowerCase().includes(searchLower)
          );
        }
        
        setTemplates(filteredTemplates);
        setTotal(response.data.pagination.total);
      }
    } catch (error) {
      console.error('加载模板列表失败:', error);
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始加载和参数变化时重新加载
  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchText, category]);

  /**
   * 删除模板
   */
  const handleDelete = async (id: string) => {
    try {
      await deleteTemplate(id);
      message.success('删除成功');
      loadTemplates();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  /**
   * 批量删除模板
   */
  const handleBatchDelete = async () => {
    try {
      const ids = selectedRowKeys.map(key => String(key));
      const response = await batchDeleteTemplates(ids);
      message.success(response.message);
      setSelectedRowKeys([]);
      loadTemplates();
    } catch (error: any) {
      console.error('批量删除失败:', error);
      message.error(error.response?.data?.message || '批量删除失败');
    }
  };

  /**
   * 表格列定义
   */
  const columns: ColumnsType<Template> = [
    {
      title: '预览图',
      dataIndex: 'previewImage',
      key: 'preview',
      width: 100,
      render: (previewImage: string) => {
        return previewImage ? (
          <Image
            src={previewImage}
            alt="预览"
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <div
            style={{
              width: 60,
              height: 60,
              background: 'var(--admin-fill)',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              color: 'var(--admin-secondary)',
            }}
          >
            无图片
          </div>
        );
      },
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: '提示词',
      dataIndex: 'prompt',
      key: 'prompt',
      ellipsis: true,
      render: (text: string) => (
        <span style={{ fontSize: 12, color: 'var(--admin-secondary)' }}>
          {text.length > 100 ? text.substring(0, 100) + '...' : text}
        </span>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags: string[]) => (
        <Space size={[0, 4]} wrap>
          {tags?.map((tag) => (
            <Tag key={tag} color="blue">
              {tag}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      align: 'center',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      render: (time: number) => dayjs.unix(time).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {hasPermission('update') && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/templates/edit/${record.id}`)}
            >
              编辑
            </Button>
          )}
          {hasPermission('delete') && (
            <Popconfirm
              title="确认删除"
              description={`确定要删除模板 "${record.title}" 吗？`}
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 页面标题和操作栏 */}
      <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '12px' }}>
        <Title level={3} style={{ margin: 0 }}>
          模板管理
        </Title>
        <Space>
          {hasPermission('create') && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/templates/create')}
            >
              <span className="btn-text">创建模板</span>
            </Button>
          )}
        </Space>
      </div>

      {/* 搜索和筛选栏 */}
      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }} size={[8, 8]}>
        <Input
          placeholder="搜索标题或提示词"
          prefix={<SearchOutlined />}
          style={{ minWidth: 200, maxWidth: '100%' }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <Select
          placeholder="选择分类"
          style={{ minWidth: 120 }}
          value={category}
          onChange={setCategory}
          allowClear
        >
          {CATEGORY_OPTIONS.map((cat) => (
            <Select.Option key={cat.value} value={cat.value}>
              {cat.label}
            </Select.Option>
          ))}
        </Select>
        <Button icon={<ReloadOutlined />} onClick={loadTemplates}>
          <span className="btn-text">刷新</span>
        </Button>
        {hasPermission('delete') && (
          <Popconfirm
            title="确认批量删除"
            description={`确定要删除选中的 ${selectedRowKeys.length} 个模板吗？`}
            onConfirm={handleBatchDelete}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            disabled={selectedRowKeys.length === 0}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={selectedRowKeys.length === 0}
            >
              <span className="btn-text">批量删除 {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}</span>
            </Button>
          </Popconfirm>
        )}
      </Space>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={templates}
        rowKey="id"
        loading={loading}
        rowSelection={
          hasPermission('delete')
            ? {
                selectedRowKeys,
                onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
                selections: [
                  Table.SELECTION_ALL,
                  Table.SELECTION_INVERT,
                  Table.SELECTION_NONE,
                ],
              }
            : undefined
        }
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
          },
        }}
        scroll={{ x: 1000 }}
      />

      {/* 响应式样式 */}
      <style>{`
        /* 小屏幕隐藏按钮文字 */}
        @media (max-width: 576px) {
          .btn-text {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default TemplateList;

